import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import type { ArticleData } from "@/mocks/articles";

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const publishDate = "2026-06-01";
  const modifiedDate = "2026-06-25";

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Header */}
        <section className="pt-24 pb-12 md:pt-32 md:pb-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <span className="inline-block text-xs font-semibold text-foreground-400 uppercase tracking-widest mb-3">
              Article · Updated June 2026
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-base md:text-lg max-w-2xl leading-relaxed">
              {article.subtitle}
            </p>
          </div>
        </section>

        {/* Hero Image */}
        <section className="bg-background-50 pb-8">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="relative w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden">
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">

            {/* Schema.org Article */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "@id": `https://allchinabuy.art/articles/${article.slug}#article`,
                  "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `https://allchinabuy.art/articles/${article.slug}`
                  },
                  "headline": article.title,
                  "description": article.description,
                  "image": article.heroImage,
                  "datePublished": publishDate,
                  "dateModified": modifiedDate,
                  "author": {
                    "@type": "Organization",
                    "name": "allchinabuy.art Editorial Team",
                    "url": "https://allchinabuy.art/about"
                  },
                  "publisher": {
                    "@type": "Organization",
                    "name": "allchinabuy.art",
                    "url": "https://allchinabuy.art/",
                    "logo": {
                      "@type": "ImageObject",
                      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg"
                    }
                  },
                  "inLanguage": "en-US"
                }),
              }}
            />

            {/* Schema.org BreadcrumbList */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "name": "Home",
                      "item": "https://allchinabuy.art/"
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": article.title,
                      "item": `https://allchinabuy.art/articles/${article.slug}`
                    }
                  ]
                }),
              }}
            />

            {article.sections.map((section, sIdx) => (
              <section key={sIdx} className="mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            {/* CTA */}
            <div className="bg-background-100 rounded-xl p-8 md:p-10 text-center border border-background-200/70 mt-12">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                Ready to Explore More?
              </h3>
              <p className="text-foreground-500 text-sm mb-6 max-w-lg mx-auto">
                {article.ctaText}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  AllChinaBuy Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="mt-8 text-xs text-foreground-400" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 flex-wrap">
                <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link></li>
                <li><i className="ri-arrow-right-s-line text-[10px]"></i></li>
                <li className="text-foreground-600">{article.title}</li>
              </ol>
            </nav>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}